import { GridBuilder } from "@repo/design-system/components/grids";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLazyGetStudentNotificationListQuery } from "@gyandiksha/shared-ui/app/services";
import { createViewStudentNotificationGridConfig } from "./config/ViewStudentNotificationGrid.config";
import "./ViewStudentNotificationGrid.scss";

const ViewStudentNotificationGrid = ({ onView, onViewImage, pageSize = 10 }) => {
    const [pageState, setPageState] = useState({ first: 0, rows: pageSize, page: 0 });
    const sortRef = useRef({ sortBy: "id", sortDir: "asc" });
    const searchRef = useRef("");

    const [getList, { data, isFetching, isError, isUninitialized }] = useLazyGetStudentNotificationListQuery();

    const fetchList = useCallback((
        page = 0,
        size = pageSize,
        search = searchRef.current,
        sort = sortRef.current,
    ) => {
        getList({ page, size, searchText: search, sortBy: sort.sortBy, sortDir: sort.sortDir });
    }, [getList, pageSize]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const notifications = data?.data?.content ?? [];
    const totalRecords = data?.data?.totalRecords ?? 0;
    // transformSuccessResponse already surfaces app-level failures as a toast and resolves with
    // null data (not an RTK error) — an empty response after a completed fetch means that path,
    // same as isError does for network/HTTP failures.
    const hasError = isError || (!isUninitialized && !isFetching && !data);

    const handlePage = useCallback((event) => {
        setPageState({ first: event.first, rows: event.rows, page: event.page });
        fetchList(event.page, event.rows, searchRef.current, sortRef.current);
    }, [fetchList]);

    const handleSort = useCallback(({ sortField, sortDir }) => {
        const nextSort = {
            sortBy: sortField || "id",
            sortDir: sortDir || "asc",
        };
        sortRef.current = nextSort;
        setPageState((previous) => ({ ...previous, first: 0, page: 0 }));
        fetchList(0, pageState.rows, searchRef.current, nextSort);
    }, [fetchList, pageState.rows]);

    const handleSearch = useCallback((value) => {
        const nextSearch = value?.trim() || "";
        searchRef.current = nextSearch;
        setPageState((previous) => ({ ...previous, first: 0, page: 0 }));
        fetchList(0, pageState.rows, nextSearch, sortRef.current);
    }, [fetchList, pageState.rows]);

    const gridConfig = useMemo(
        () => createViewStudentNotificationGridConfig({ onViewImage }),
        [onViewImage]
    );

    return (
        <GridBuilder
            dataSource={notifications}
            config={gridConfig}
            actionHandlers={{ VIEW: onView }}
            loading={isFetching}
            paginator={totalRecords > 0}
            lazy
            totalRecords={totalRecords}
            first={pageState.first}
            rows={pageState.rows}
            rowsPerPageOptions={[5, 10, 25, 50]}
            onPage={handlePage}
            onSort={handleSort}
            onSearch={handleSearch}
            noRecordFoundMessage={hasError
                ? "Unable to load notifications. Please try again."
                : "No notifications found"}
            scrollHeight="58vh"
            tableStyle={{ minWidth: "100%" }}
        />
    );
};

ViewStudentNotificationGrid.propTypes = {
    onView: PropTypes.func.isRequired,
    onViewImage: PropTypes.func,
    pageSize: PropTypes.number,
};

export default React.memo(ViewStudentNotificationGrid);
